import { css } from 'goober';
import type { IProductItem } from './ProductCard';
import ProductCard from './ProductCard';

// Import Swiper React components and required modules
import { Keyboard, Mousewheel, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { MessagesItemAssistant } from '../AgentChatbotRC/styles';

// CSS with essential Swiper styles included inline to prevent import issues
const productListContainerStyle = css`
    padding: 12px 0;
    width: 100%;

    /* Single product container styles */
    .single-product-container {
        display: flex;
        justify-content: center;
        width: 100%;
    }

    /* Essential Swiper base styles */
    .swiper {
        margin-left: auto;
        margin-right: auto;
        position: relative;
        overflow: hidden;
        list-style: none;
        padding: 0;
        z-index: 1;
        display: block;
        touch-action: pan-y pinch-zoom;
    }

    .swiper-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: flex;
        transition-property: transform;
        transition-timing-function: var(
            --swiper-wrapper-transition-timing-function,
            initial
        );
        box-sizing: content-box;
        touch-action: pan-y pinch-zoom;
    }

    .swiper-slide {
        flex-shrink: 0;
        width: 100%;
        height: 100%;
        position: relative;
        transition-property: transform;
        display: block;
        touch-action: auto;
    }

    /* Scoped Swiper CSS to prevent conflicts */
    .bb-product-swiper {
        width: 100%;
        height: auto;
        touch-action: pan-y pinch-zoom;

        .swiper-slide {
            height: auto !important;
            touch-action: auto;
        }

        /* Prevent interference with vertical scrolling */
        &.swiper-horizontal {
            touch-action: pan-y pinch-zoom;
        }

        /* Only capture horizontal swipes */
        .swiper-wrapper {
            touch-action: pan-y pinch-zoom;
        }

        /* Scoped pagination styles */
        .swiper-pagination {
            position: static !important;
            margin-top: 16px;
            text-align: center;
            transition: 0.3s opacity;
            transform: translate3d(0, 0, 0);
            z-index: 10;

            .swiper-pagination-bullet {
                width: 8px;
                height: 8px;
                display: inline-block;
                border-radius: 50%;
                background-color: var(--bb-primary, #2e0a57) !important;
                opacity: 0.3 !important;
                margin: 0 4px;
                cursor: pointer;
                transition: opacity 0.3s;

                &.swiper-pagination-bullet-active {
                    opacity: 1 !important;
                }

                /* Only enable hover on non-touch devices */
                @media (hover: hover) and (pointer: fine) {
                    &:hover {
                        opacity: 0.7 !important;
                    }
                }
            }
        }

        /* Scoped navigation styles */
        .swiper-button-next,
        .swiper-button-prev {
            position: absolute;
            top: 50%;
            width: 40px;
            height: 40px;
            margin-top: -20px;
            z-index: 10;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            color: var(--bb-primary, #2e0a57) !important;
            background-color: white;
            border-radius: 50%;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            transition: all 0.3s ease;

            &:after {
                font-family: swiper-icons;
                font-size: 16px !important;
                text-transform: none !important;
                letter-spacing: 0;
                font-variant: initial;
                line-height: 1;
                font-weight: bold;
            }

            /* Only enable hover on non-touch devices */
            @media (hover: hover) and (pointer: fine) {
                &:hover {
                    background-color: var(--bb-primary, #2e0a57);
                    color: white !important;
                    transform: scale(1.1);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

                    &:after {
                        color: white !important;
                    }
                }
            }

            @media (max-width: 768px) {
                display: none !important;
            }
        }

        .swiper-button-next {
            right: 10px !important;

            &:after {
                content: '→';
            }
        }

        .swiper-button-prev {
            left: 10px !important;

            &:after {
                content: '←';
            }
        }

        .swiper-button-disabled {
            opacity: 0.35;
            cursor: auto;
            pointer-events: none;

            @media (hover: hover) and (pointer: fine) {
                &:hover {
                    background-color: white;
                    color: var(--bb-primary, #2e0a57) !important;
                    transform: none;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
            }
        }
    }
`;

interface IProductsList {
    products: IProductItem[];
    onChange: (product: IProductItem, type: 'view' | 'addToCart') => void;
    introMessage?: string;
}

export const ProductsList = ({
    products,
    onChange,
    introMessage
}: IProductsList) => {
    // Multiple products - render with carousel
    return (
        <div className={productListContainerStyle}>
            {introMessage && (
                <div className={MessagesItemAssistant}>{introMessage}</div>
            )}

            {(products ?? []).length === 1 ? (
                <div
                    className='single-product-container'
                    style={{ marginTop: '1rem' }}
                >
                    <ProductCard product={products[0]} onChange={onChange} />
                </div>
            ) : (products ?? []).length > 1 ? (
                <Swiper
                    className='bb-product-swiper'
                    style={{ marginTop: '1rem' }}
                    modules={[Navigation, Pagination, Mousewheel, Keyboard]}
                    slidesPerView={2.5}
                    spaceBetween={16}
                    pagination={{
                        clickable: true
                    }}
                    navigation={true}
                    mousewheel={{
                        forceToAxis: true,
                        sensitivity: 1,
                        releaseOnEdges: true
                    }}
                    keyboard={{
                        enabled: true,
                        onlyInViewport: true
                    }}
                    grabCursor={true}
                    touchRatio={1}
                    touchAngle={45}
                    threshold={10}
                    longSwipesRatio={0.5}
                    longSwipesMs={300}
                    followFinger={true}
                    allowTouchMove={true}
                    touchStartPreventDefault={false}
                    touchStartForcePreventDefault={false}
                    touchMoveStopPropagation={false}
                    preventClicks={false}
                    preventClicksPropagation={false}
                    resistanceRatio={0.85}
                    touchEventsTarget='container'
                    passiveListeners={false}
                    simulateTouch={true}
                    shortSwipes={true}
                    longSwipes={true}
                    edgeSwipeDetection={false}
                    edgeSwipeThreshold={20}
                    preventInteractionOnTransition={false}
                    breakpoints={{
                        320: {
                            slidesPerView: 1.5,
                            spaceBetween: 12
                        },
                        640: {
                            slidesPerView: 2.5,
                            spaceBetween: 16
                        },
                        768: {
                            slidesPerView: 2.5,
                            spaceBetween: 16
                        },
                        1024: {
                            slidesPerView: 2.5,
                            spaceBetween: 20
                        },
                        1280: {
                            slidesPerView: 2.5,
                            spaceBetween: 20
                        }
                    }}
                >
                    {(products ?? []).map((product, i: number) => (
                        <SwiperSlide key={i}>
                            <ProductCard
                                product={product}
                                onChange={onChange}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            ) : null}
        </div>
    );
};

// Re-export for backward compatibility
export type { IProductItem };
export default ProductCard;
